/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import addProductInformationFilter from "@stoneage/App/Handlers/Documents/addProductInformationFilter";
import getProductInformationDocuments from "@stoneage/App/Handlers/Documents/getProductInformationDocuments";
import { ProductInformationDocumentsApiParameters } from "@stoneage/App/Services/Documents/DocumentsService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);
    return {
        isLoading: state.xProductInfo.isLoading,
        documents: state.xProductInfo.data?.productDocuments,
        location,
        getDocumentParams: state.xProductInfo.filters,
        isAuthenticated: state.context.session.isAuthenticated,
    };
};

const mapDispatchToProps = {
    getProductInformationDocuments,
    addProductInformationFilter,
};
export interface ProductInformationPageStyles {
    page?: MobiusPageProps;
}

export const productInformationPageStyles: ProductInformationPageStyles = {};
const styles = productInformationPageStyles;

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const ProductInformationPage = ({
    id,
    isLoading,
    getProductInformationDocuments,
    addProductInformationFilter,
    location,
    getDocumentParams,
    history,
    isAuthenticated,
}: Props) => {
    const [firstLoad, setFirstLoaded] = React.useState(false);
    useEffect(() => {
        if (!firstLoad) {
            setFirstLoaded(true);
            const urlParams = parseQueryString<ProductInformationDocumentsApiParameters>(location.search);
            const apiParameters: ProductInformationDocumentsApiParameters = {
                doFacetedSearches: true,
                page: urlParams.page,
                pageSize: urlParams.pageSize,
                sortBy: urlParams.sortBy,
                query: urlParams.query,
            };
            if (urlParams.onlyShowMyProducts) {
                if (!isAuthenticated) {
                    delete urlParams.onlyShowMyProducts;
                    return;
                }
                apiParameters.onlyShowMyProducts = urlParams.onlyShowMyProducts;
                addProductInformationFilter({ filterType: "OnlyShowMyProducts", value: urlParams.onlyShowMyProducts });
            }
            if (urlParams.onlyShowActiveDocuments) {
                apiParameters.onlyShowActiveDocuments =
                    (urlParams.onlyShowActiveDocuments as unknown as string) === "true";
                addProductInformationFilter({
                    filterType: "OnlyShowActiveDocuments",
                    value: (urlParams.onlyShowActiveDocuments as unknown as string) === "true",
                });
            }
            if (urlParams.onlyShowDiscontinuedDocuments) {
                apiParameters.onlyShowDiscontinuedDocuments =
                    (urlParams.onlyShowDiscontinuedDocuments as unknown as string) === "true";
                addProductInformationFilter({
                    filterType: "OnlyShowDiscontinuedDocuments",
                    value: (urlParams.onlyShowDiscontinuedDocuments as unknown as string) === "true",
                });
            }
            if (urlParams.applications?.length) {
                apiParameters.applications = urlParams.applications;
                addProductInformationFilter({ filterType: "Application", value: urlParams.applications });
            }
            if (urlParams.documentTypes?.length) {
                apiParameters.documentTypes = urlParams.documentTypes;
                addProductInformationFilter({ filterType: "DocumentTypes", value: urlParams.documentTypes });
            }
            if (urlParams.productLines?.length) {
                apiParameters.productLines = urlParams.productLines;
                addProductInformationFilter({ filterType: "ProductLines", value: urlParams.productLines });
            }

            if (urlParams.query?.length) {
                apiParameters.query = urlParams.query;
                addProductInformationFilter({ filterType: "Query", value: urlParams.query });
            }
        }
    }, []);

    useEffect(() => {
        const apiParameters: ProductInformationDocumentsApiParameters = {
            doFacetedSearches: true,
            onlyShowActiveDocuments: getDocumentParams.onlyShowActiveDocuments,
            onlyShowDiscontinuedDocuments: getDocumentParams.onlyShowDiscontinuedDocuments,
            page: getDocumentParams.page,
            pageSize: getDocumentParams.pageSize,
            sortBy: getDocumentParams.sortBy,
            query: getDocumentParams.query,
        };
        if (isAuthenticated) {
            apiParameters.onlyShowMyProducts = getDocumentParams.onlyShowMyProducts;
        }
        if (getDocumentParams.applications?.length) {
            apiParameters.applications = getDocumentParams.applications.join();
        }
        if (getDocumentParams.documentTypes?.length) {
            apiParameters.documentTypes = getDocumentParams.documentTypes.join();
        }
        if (getDocumentParams.productLines?.length) {
            apiParameters.productLines = getDocumentParams.productLines.join();
        }
        if (getDocumentParams.query?.length) {
            apiParameters.query = getDocumentParams.query;
        }
        getProductInformationDocuments({ apiParameters });

        const queryString = qs.stringify(apiParameters);
        history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
    }, [getDocumentParams]);

    return (
        <LoadingOverlay loading={isLoading === true}>
            <Page data-test-selector="productInformationPage" {...styles.page}>
                <Zone contentId={id} zoneName="Content" />
                <Modals />
            </Page>
        </LoadingOverlay>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductInformationPage)),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
    },
};

export default pageModule;

export const ProductInformationPageContext = "ProductInformationPage";
